<!-- src/app/pages/phone-bank/phone-bank.component.html -->
<section class="pb-page">
  <div class="container">

    <!-- ── HERO ─────────────────────────────────────────────────────────── -->
    <div class="hero">
      <h1>Phone Banking</h1>
      <p class="hero-sub">Call Michigan voters, share Lydia's story, and ask for donations to fuel the campaign.</p>
    </div>

    <!-- ── HOW IT WORKS ──────────────────────────────────────────────────── -->
    <div *ngIf="step === 'signin'" class="how-it-works">
      <div class="hiw-step"><span class="hiw-num">1</span><p>Sign in with your name and email below.</p></div>
      <div class="hiw-step"><span class="hiw-num">2</span><p>Get phone numbers from your captain or the volunteer list (emailed to you).</p></div>
      <div class="hiw-step"><span class="hiw-num">3</span><p>Read the script, dial the number from your phone, and log the result here.</p></div>
      <div class="hiw-step"><span class="hiw-num">4</span><p>Repeat! Every dollar raised helps Lydia reach more Michigan voters.</p></div>
    </div>

    <!-- ── SIGN IN ───────────────────────────────────────────────────────── -->
    <div *ngIf="step === 'signin'" class="signin-card">
      <h2>Sign In to Start</h2>
      <div class="form-group">
        <label for="vName">Your Name</label>
        <input id="vName" type="text" [(ngModel)]="volunteerName" placeholder="Full name" />
      </div>
      <div class="form-group">
        <label for="vEmail">Your Email</label>
        <input id="vEmail" type="email" [(ngModel)]="volunteerEmail" placeholder="your@email.com" />
      </div>
      <button class="btn-start" (click)="startSession()" [disabled]="!volunteerName.trim() || !volunteerEmail.trim()">
        Start Phone Banking
      </button>
    </div>

    <!-- ── ACTIVE CALLING SCREEN ──────────────────────────────────────────── -->
    <div *ngIf="step === 'calling'" class="calling-layout">

      <!-- SESSION BAR -->
      <div class="session-bar">
        <span class="session-bar__volunteer">📞 {{ volunteerName }}</span>
        <span class="session-bar__count">{{ callCount }} call{{ callCount !== 1 ? 's' : '' }} logged this session</span>
        <button class="session-bar__end" (click)="endSession()">End Session</button>
      </div>

      <!-- TWO COLUMNS: SCRIPT + DIALER -->
      <div class="calling-grid">

        <!-- SCRIPT PANEL -->
        <div class="script-panel" [class.script-panel--collapsed]="!scriptOpen">
          <button class="script-toggle" (click)="toggleScript()">
            {{ scriptOpen ? '◀ Hide Script' : '▶ Show Script' }}
          </button>
          <div *ngIf="scriptOpen" class="script-body">
            <h2 class="script-title">📋 Call Script</h2>

            <div class="script-section">
              <h3>Opening</h3>
              <div class="script-line">
                <p>"Hi, is this [Name]? My name is <strong>{{ volunteerName }}</strong>, and I'm a volunteer for <strong>Lydia Christensen</strong>, who is running as the Libertarian Party nominee for U.S. Senate in Michigan."</p>
              </div>
              <div class="script-line script-line--pause">
                <em>Wait for response. If they say yes, continue. If bad time, ask: "When would be a better time to call back?"</em>
              </div>
            </div>

            <div class="script-section">
              <h3>The Ask</h3>
              <div class="script-line">
                <p>"Lydia is running as the Libertarian Party nominee - no corporate donors, just fighting for Michigan. She's raising money entirely from people like you to get her message out to voters across the state. <strong>Would you be willing to make a small donation to her campaign?</strong>"</p>
              </div>
              <div class="script-line script-line--yes">
                <strong>If YES:</strong> "That's wonderful! You can donate at <strong>lydialynnchristensen.com/donate</strong> - even $25 makes a real difference for a third-party candidate with no corporate money behind her."
              </div>
              <div class="script-line script-line--no">
                <strong>If UNSURE:</strong> "No problem - can I share a little about what she stands for?"
              </div>
            </div>

            <div class="script-section">
              <h3>Key Talking Points</h3>
              <ul class="script-points">
                <li><strong>Great Lakes & Environment:</strong> Lydia is committed to protecting Michigan's water - no corporate influence, no compromise.</li>
                <li><strong>No Corporate Donors:</strong> She's funded by people like you, not PACs or corporate bosses. Every dollar stays in Michigan.</li>
                <li><strong>UP & Rural Michigan:</strong> She lives in Ironwood and understands the UP is constantly overlooked in Lansing and D.C.</li>
                <li><strong>Transparency:</strong> Her legislative tracker lets you watch every bill your senators vote on - in real time.</li>
                <li><strong>LGBTQ+ Rights:</strong> Lydia is a proud ally and believes everyone deserves dignity and equal protection.</li>
              </ul>
            </div>

            <div class="script-section">
              <h3>Handling Objections</h3>
              <div class="script-line">
                <strong>"I don't donate to campaigns."</strong><br>
                <em>"I completely understand. Even just sharing her website with someone you know helps. Every bit of support counts for a Libertarian with no corporate money behind her."</em>
              </div>
              <div class="script-line">
                <strong>"I've never heard of her."</strong><br>
                <em>"She's a grassroots candidate - no big money behind her. You can read everything about her platform at lydialynnchristensen.com. She's the real deal."</em>
              </div>
              <div class="script-line">
                <strong>"I'm not interested."</strong><br>
                <em>"No worries at all. Thank you for your time and have a great day!"</em>
              </div>
            </div>

            <div class="script-section">
              <h3>Close</h3>
              <div class="script-line">
                <p>"Thank you so much for your time, [Name]. If you'd like to support the campaign, you can donate at <strong>lydialynnchristensen.com/donate</strong> or learn more about Lydia's platform at <strong>lydialynnchristensen.com</strong>. Every Michigander counts. Have a wonderful day!"</p>
              </div>
            </div>
          </div>
        </div>

        <!-- DIALER PANEL -->
        <div class="dialer-panel">

          <!-- ENTER NUMBER -->
          <div class="dialer-card" *ngIf="!dialedNumber">
            <h2 class="dialer-card__title">Enter the number you're about to call</h2>
            <p class="dialer-card__hint">Get numbers from your volunteer list or captain. Dial from your own phone.</p>
            <div class="number-input-row">
              <input
                type="tel"
                [(ngModel)]="phoneNumber"
                placeholder="(555) 555-5555"
                class="number-input"
                (keyup.enter)="markDialed()"
              />
              <button class="btn-dialed" (click)="markDialed()" [disabled]="!phoneNumber.trim()">
                I Dialed ↗
              </button>
            </div>
          </div>

          <!-- LOG RESULT -->
          <div class="dialer-card" *ngIf="dialedNumber">
            <div class="dialing-number">
              <span class="dialing-number__label">Called:</span>
              <span class="dialing-number__val">{{ dialedNumber }}</span>
              <button class="dialing-number__change" (click)="dialedNumber = ''">change</button>
            </div>

            <h3 class="result-label">What happened?</h3>
            <div class="outcome-grid">
              <button class="outcome-btn outcome-btn--answered" [class.outcome-btn--selected]="outcome === 'answered'" (click)="selectOutcome('answered')">
                ✅ Answered
              </button>
              <button class="outcome-btn outcome-btn--voicemail" [class.outcome-btn--selected]="outcome === 'voicemail'" (click)="selectOutcome('voicemail')">
                📬 Voicemail
              </button>
              <button class="outcome-btn outcome-btn--no-answer" [class.outcome-btn--selected]="outcome === 'no-answer'" (click)="selectOutcome('no-answer')">
                📵 No Answer
              </button>
              <button class="outcome-btn outcome-btn--dnc" [class.outcome-btn--selected]="outcome === 'do-not-call'" (click)="selectOutcome('do-not-call')">
                🚫 Do Not Call
              </button>
            </div>

            <!-- Answered follow-ups -->
            <div *ngIf="outcome === 'answered'" class="followup-questions">
              <div class="form-group">
                <label>Were they interested in supporting Lydia?</label>
                <div class="radio-row">
                  <label><input type="radio" [(ngModel)]="interested" name="interested" value="Yes" /> Yes</label>
                  <label><input type="radio" [(ngModel)]="interested" name="interested" value="Maybe" /> Maybe</label>
                  <label><input type="radio" [(ngModel)]="interested" name="interested" value="No" /> No</label>
                </div>
              </div>
              <div class="form-group">
                <label>Do they want more information?</label>
                <div class="radio-row">
                  <label><input type="radio" [(ngModel)]="wantsInfo" name="wantsInfo" value="Yes" /> Yes</label>
                  <label><input type="radio" [(ngModel)]="wantsInfo" name="wantsInfo" value="No" /> No</label>
                </div>
              </div>
              <div class="form-group form-group--check">
                <label>
                  <input type="checkbox" [(ngModel)]="callbackRequested" name="callback" />
                  Requested a callback
                </label>
              </div>
            </div>

            <!-- Notes -->
            <div class="form-group">
              <label>Notes (optional)</label>
              <textarea [(ngModel)]="notes" name="notes" placeholder="Anything worth noting about this call..." rows="3"></textarea>
            </div>

            <div *ngIf="errorMessage" class="error-msg">{{ errorMessage }}</div>

            <button
              class="btn-log"
              (click)="logResult()"
              [disabled]="!canSubmit || isSubmitting"
            >
              {{ isSubmitting ? 'Logging...' : 'Log Result & Next Call →' }}
            </button>
          </div>

          <!-- SESSION LOG -->
          <div *ngIf="sessionLog.length > 0" class="session-log">
            <h3 class="session-log__title">This Session</h3>
            <div *ngFor="let log of sessionLog" class="log-entry">
              <span class="log-entry__icon">{{ outcomeIcon(log.outcome) }}</span>
              <span class="log-entry__number">{{ log.number }}</span>
              <span class="log-entry__outcome">{{ log.outcome }}</span>
              <span class="log-entry__time">{{ log.time }}</span>
            </div>
          </div>

        </div>
      </div>
    </div>

  </div>
</section>
